import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { SvpEntity } from '../../../svp/models/svp.model';
import { environment } from '../../../../environments/environment';
import { NotificationService } from '../../../core/services/notification-service/notification-service';
import { SvpDetailService } from '../svp-detail-service/svp-detail.service';
import { SvpService } from '../../../svp/services/svp-service/svp-service';
import { isSvpLockConflict } from '../../../core/models/problem-detail.model';
import { describeApiError } from '../../../core/errors/api-error';
import { ApiErrorHandler } from '../../../core/errors/api-error-handler';

/**
 * Owns the ŠVP lock/unlock concern: whether the current viewer may toggle the lock,
 * performing the toggle, and reporting the 409 conflicts that subtree mutations hit when
 * they write to a locked ŠVP. Reads the active ŠVP and reloads it via {@link SvpDetailService};
 * also refreshes the ŠVP list so its lock indicators stay in sync.
 */
@Injectable({
  providedIn: 'root',
})
export class SvpLockService {
  private readonly http = inject(HttpClient);
  private readonly notificationService = inject(NotificationService);
  private readonly apiErrors = inject(ApiErrorHandler);
  private readonly svpDetailService = inject(SvpDetailService);
  private readonly svpService = inject(SvpService);

  // Server-computed per viewer (school admin of this ŠVP's school, or system admin).
  readonly canToggleLock = computed(() => this.svpDetailService.svp()?.canToggleLock ?? false);

  readonly #toggling = signal(false);
  readonly isToggling = this.#toggling.asReadonly();

  toggleLock(svpId: string, locked: boolean): void {
    this.#toggling.set(true);
    this.http.put<SvpEntity>(`${environment.apiUrl}/svps/${svpId}/lock`, { locked }).subscribe({
      next: () => {
        this.#toggling.set(false);
        this.svpDetailService.reload();
        this.svpService.reloadSvps();
        this.notificationService.showSuccess(locked ? 'ŠVP byl uzamčen' : 'ŠVP byl odemčen');
      },
      error: (error: HttpErrorResponse) => {
        this.#toggling.set(false);
        this.apiErrors.notify(error, 'SvpLockService.toggleLock');
      },
    });
  }

  /** @see isSvpLockConflict — exposed on the service so call sites that already inject it can reuse it. */
  isLockConflict(error: HttpErrorResponse): boolean {
    return isSvpLockConflict(error);
  }

  handleLockConflict(error: HttpErrorResponse): void {
    this.notificationService.showPersistentError(describeApiError(error).message, 'Zavřít');
    this.svpDetailService.reload();
    this.svpService.reloadSvps();
  }
}
